import React, { useEffect, useState, useRef, useMemo } from 'react';
import T from 'i18n';
import {
  Chip,
  IconButton,
  Stack,
  TextField,
  Typography,
  Button,
  Tooltip,
  Popper,
  Paper,
  ClickAwayListener,
} from '@mui/material';
import {
  ConstructionOutlined,
  FilterListOutlined,
  SendOutlined,
  TuneOutlined,
  MarginOutlined,
  Compare,
  FolderOutlined,
  SettingsBackupRestoreOutlined,
  CloseOutlined,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useDrop } from 'react-dnd';
import { selectCurrentCaseId, selectUseAiPromptLibrary } from 'common/selectors';
import history from 'common/history';
import { useFetchMatches } from '../redux/fetchMatches';
import { useGenerateAnswers } from '../redux/generateAnswers';
import SearchOptions from './SearchOptions';
import RecentSearchesList from './RecentSearchesList';
import TrialBundlesMenu from './TrialBundlesMenu';
import OutputFormatMenu from './OutputFormatMenu';
import FilesAndBundlesSelectionDialog from './FilesAndBundlesSelectionDialog';
import AiToolsDialog from './AiToolsDialog';
import useAIHandler from './hooks/useAIHandler';
import { useSetAISearchState } from '../redux/setAISearchState';

interface SearchInputProps {
  handleSearch: (input: string, filters: any, outputType?: string) => void;
  isNewSearch?: boolean;
  matches?: Array<any>;
  isAnswersMode?: boolean;
  fileSidebarAskAI?: boolean;
  gridView?: boolean;
  file?: any;
  inputFilters?: any;
  fileData?: any;
  directAnswers?: boolean;
  askAiSearchType?: string;
  initialOutputFormat?: string;
  searchAnsChat?: any;
  onOutputFormatChange?: (format: string) => void;
}

interface FileItem {
  id: string;
  name: string;
  pageCount?: string;
  size?: number;
  type?: string;
  originalName?: string;
  fileSidebarAskAI?: boolean;
}

const SearchFiltersChip = ({ filters, isAnswersMode, file, type, fileSidebarAskAI }: any) => {
  if (type === 'location' && filters.location.length === 0) {
    return (
      <Typography sx={{ display: 'inline', color: 'GrayText' }}>
        <b>{T.translate('case.bundles')}</b>
      </Typography>
    );
  } else if ((type === 'files' || type === 'compareFiles') && filters.files.length === 0) {
    return (
      <Typography sx={{ display: 'inline', color: 'GrayText' }}>
        <b>
          {(fileSidebarAskAI && file) || type === 'compareFiles'
            ? T.translate('generic.compare')
            : T.translate('case.documents')}
        </b>
      </Typography>
    );
  }
  const truncateText = (text: string, maxLength: number = fileSidebarAskAI ? 8 : 10) => {
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  return (
    <>
      {isAnswersMode && type === 'location' && filters.location.length > 0 ? (
        <Typography sx={{ display: 'inline', color: 'GrayText' }}>
          <b>{T.translate('case.bundles')}</b> : {truncateText(filters.location[0].name)}
          {filters.location.length > 1 ? ` & ${filters.location.length - 1} more ...` : ''}{' '}
        </Typography>
      ) : !isAnswersMode && type === 'location' && filters.location.length > 0 ? (
        <Typography sx={{ display: 'inline', color: 'GrayText' }}>
          <b>{T.translate('case.bundles')}</b> : {truncateText(filters.location[0].name)}
          {filters.location.length > 1 ? ` & ${filters.location.length - 1} more ...` : ''}{' '}
        </Typography>
      ) : isAnswersMode && type === 'files' && filters.files.length > 0 ? (
        <Typography sx={{ display: 'inline', color: 'GrayText' }}>
          <b>{T.translate('generic.doc')}</b> : {truncateText(filters.files[0]?.name)}
          {filters.files.length > 1 ? ` & ${filters.files.length - 1} more ...` : ''}
        </Typography>
      ) : isAnswersMode && type === 'compareFiles' && filters.files.length > 0 ? (
        <Typography sx={{ display: 'inline', color: '#000' }}>
          <b>{T.translate('generic.compare')}</b> : {truncateText(filters.files[0]?.name)}
          {filters.files.length > 1 ? ` & ${filters.files.length - 1} more ...` : ''}
        </Typography>
      ) : !isAnswersMode &&
        (type === 'files' || type === 'compareFiles') &&
        filters.files.length > 0 ? (
        <Typography sx={{ display: 'inline', color: '#000' }}>
          <b>
            {(fileSidebarAskAI && file) || type === 'compareFiles'
              ? T.translate('generic.compare')
              : T.translate('generic.doc')}
          </b>{' '}
          : {truncateText(filters.files[0]?.name)}
          {filters.files.length > 1 ? ` & ${filters.files.length - 1} more ...` : ''}
        </Typography>
      ) : !isAnswersMode &&
        type === 'files' &&
        filters.isCompareMode &&
        filters.files.length > 0 ? (
        <Typography sx={{ display: 'inline', color: '#000' }}>
          <b>{T.translate('generic.compare')}</b> : {truncateText(filters.files[0]?.name)}
          {filters.files.length > 1 ? ` & ${filters.files.length - 1} more ...` : ''}
        </Typography>
      ) : null}
    </>
  );
};

export default function SearchInput({
  handleSearch,
  isNewSearch,
  isAnswersMode,
  fileSidebarAskAI,
  gridView,
  file,
  inputFilters,
  fileData,
  directAnswers,
  askAiSearchType,
  initialOutputFormat,
  searchAnsChat,
  onOutputFormatChange,
}: SearchInputProps) {
  const multiComparison = true;
  const inpRef = useRef<HTMLInputElement>(null);
  const outputFormatTimeoutRef = useRef<any>(null);
  const { searchMatchesInput, directAnswersFilters } = useSetAISearchState();
  const { filters, setFilters, handleChangeFilters } = useAIHandler({
    initialFilters: inputFilters,
  });
  // Drop target for bundles dragged from the sidebar
  const [{ isOverBundle, canDropBundle }, dropRef] = useDrop(
    () => ({
      accept: 'BUNDLE',
      canDrop: (item: any) => !!item?.id && !!item?.name,
      drop: (item: any) => {
        if (!item || !item.id) return;
        handleChangeFilters({
          location: (() => {
            const current = Array.isArray(filters.location) ? filters.location : [];
            // Avoid duplicates by id
            if (current.some((loc: any) => loc.id === item.id)) return current;
            return [...current, { id: item.id, name: item.name }];
          })(),
        });
      },
      collect: (monitor) => ({
        isOverBundle: monitor.isOver(),
        canDropBundle: monitor.canDrop(),
      }),
    }),
    [filters.location, handleChangeFilters],
  );
  const currentPathname = history.location.pathname;
  // Sync filters with inputFilters when inputFilters changes
  // Update files and location from inputFilters, including when they're empty arrays (for reset)
  useEffect(() => {
    if (inputFilters) {
      setFilters((prevFilters) => {
        const updates: any = {};

        // Update files if inputFilters explicitly provides files (including empty array for reset)
        if (inputFilters.files !== undefined && Array.isArray(inputFilters.files)) {
          // Check if files actually changed to avoid unnecessary updates
          const filesChanged =
            JSON.stringify(prevFilters.files) !== JSON.stringify(inputFilters.files);
          if (filesChanged) {
            updates.files = inputFilters.files;
            // Also sync isCompareMode when updating files
            if (inputFilters.isCompareMode !== undefined) {
              updates.isCompareMode = inputFilters.isCompareMode;
            }
          }
        }

        // Update location if inputFilters explicitly provides location (including empty array for reset)
        if (inputFilters.location !== undefined && Array.isArray(inputFilters.location)) {
          // Check if location actually changed to avoid unnecessary updates
          const locationChanged =
            JSON.stringify(prevFilters.location) !== JSON.stringify(inputFilters.location);
          if (locationChanged) {
            updates.location = inputFilters.location;
          }
        }

        // Apply updates if there are actual changes
        if (Object.keys(updates).length > 0) {
          return {
            ...prevFilters,
            ...updates,
          };
        }

        return prevFilters;
      });
    }
  }, [setFilters, inputFilters]);

  // Sync filters with searchAnsChat when component renders
  useEffect(() => {
    if (searchAnsChat && Array.isArray(searchAnsChat) && searchAnsChat.length > 1) {
      // Get the last object in the searchAnsChat array
      const lastSearchAnsChat = searchAnsChat[searchAnsChat.length - 1];

      setFilters((prevFilters) => {
        const updates: any = {};

        // Check if lastSearchAnsChat has filesToCompare
        if (
          lastSearchAnsChat.filesToCompare &&
          Array.isArray(lastSearchAnsChat.filesToCompare) &&
          lastSearchAnsChat.filesToCompare.length > 0
        ) {
          updates.files = lastSearchAnsChat.filesToCompare;
          updates.isCompareMode = true;
        } else {
          // Check if filters.files is empty or null and lastSearchAnsChat.files has data
          if (
            (!prevFilters.files || prevFilters.files.length === 0) &&
            lastSearchAnsChat.files &&
            Array.isArray(lastSearchAnsChat.files) &&
            lastSearchAnsChat.files.length > 0
          ) {
            updates.files = lastSearchAnsChat.files;
          }
        }

        // Check if filters.location is empty or doesn't exist and lastSearchAnsChat.location has data
        if (
          (!prevFilters.location || prevFilters.location.length === 0) &&
          lastSearchAnsChat.location &&
          Array.isArray(lastSearchAnsChat.location) &&
          lastSearchAnsChat.location.length > 0
        ) {
          updates.location = lastSearchAnsChat.location;
        }

        // Only update if there are changes
        if (Object.keys(updates).length > 0) {
          return {
            ...prevFilters,
            ...updates,
          };
        }

        return prevFilters;
      });
    }
  }, [searchAnsChat, setFilters]);

  const { fetchMatchDocumentsPending } = useFetchMatches();
  const { generateAnswersPending } = useGenerateAnswers();
  // Get current case ID from Redux store
  const caseId = useSelector(selectCurrentCaseId);
  const useAiPromptLibrary = useSelector(selectUseAiPromptLibrary);

  // Handle opening document in new tab
  const handleViewDocument = (fileId: string) => {
    const url = `/case/${caseId}/files/${fileId}?preview=true`;
    window.open(url, '_blank');
  };
  // Remove a bundle (location) by id
  const handleRemoveLocation = (locationId: string) => {
    setFilters((prev: any) => ({
      ...prev,
      location: (prev.location || []).filter((loc: any) => loc.id !== locationId),
    }));
  };
  // Remove a file by id
  const [anchorFileRemoved, setAnchorFileRemoved] = React.useState(false);

  const handleRemoveFile = (fileId: string) => {
    setFilters((prev: any) => {
      const currentFiles = prev.files || [];
      const isAnchorFile = currentFiles.length > 0 && currentFiles[0].id === fileId;
      // Check if removing anchor file in compare mode or multi-comparison mode
      if ((prev.isCompareMode || multiComparison) && isAnchorFile && currentFiles.length > 1) {
        setAnchorFileRemoved(true);
        // Auto-hide the message after 5 seconds
        setTimeout(() => setAnchorFileRemoved(false), 5000);
      }

      const updatedFiles = currentFiles.filter((f: any) => f.id !== fileId);

      // If all files are removed in compare mode, turn off compare mode
      if (prev.isCompareMode && updatedFiles.length === 0) {
        return {
          ...prev,
          files: updatedFiles,
          isCompareMode: false,
        };
      }

      return {
        ...prev,
        files: updatedFiles,
      };
    });
  };
  const [shouldRestoreFocus, setShouldRestoreFocus] = useState(false);
  const [searchOptionsAnchorEl, setSearchOptionsAnchorEl] = useState<null | HTMLElement>(null);
  const openSearchOptions = Boolean(searchOptionsAnchorEl);
  const [recentSearchesAnchorEl, setRecentSearchesAnchorEl] = useState<null | HTMLElement>(null);
  const openRecentSearches = Boolean(recentSearchesAnchorEl);
  const [filtersMenu, setFiltersMenu] = useState<null | HTMLElement>(null);
  const openFiltersMenu = Boolean(filtersMenu);
  const [outputFormatAnchorEl, setOutputFormatAnchorEl] = useState<null | HTMLElement>(null);
  const openOutputFormatMenu = Boolean(outputFormatAnchorEl);
  const [filesPopupOpen, setFilesPopupOpen] = useState(false);
  const [compareFilesMode, setCompareFilesMode] = useState(false);
  const [selectedBundlesAnchorEl, setSelectedBundlesAnchorEl] = useState<null | HTMLElement>(null);
  const openSelectedBundlesMenu = Boolean(selectedBundlesAnchorEl);
  const [aiToolsDialogOpen, setAiToolsDialogOpen] = useState(false);

  // Check if we're on the AI Tools page
  const isAiToolsPage = currentPathname.includes('/ai-tools');

  const showCompareChip = useMemo(() => {
    return (
      isAiToolsPage &&
      (filters.location?.length ?? 0) === 0 &&
      (!filters.files?.length || filters.isCompareMode)
    );
  }, [isAiToolsPage, filters.location?.length, filters.files?.length, filters.isCompareMode]);

  // Get display label for output format
  const getOutputFormatLabel = (format: string) => {
    const formatLabels: { [key: string]: string } = {
      table: 'Table',
      list: 'List',
      freeform: 'Free Form',
      memo: 'Memo',
      briefing: 'Briefing',
      chronology: 'Chronology',
    };
    return formatLabels[format] || 'List';
  };

  // Use local state only, default to initialOutputFormat or 'list'
  const [selectedOutputFormat, setSelectedOutputFormat] = useState<string>(
    initialOutputFormat || 'list',
  );
  // Track if the format was set from AiToolsDialog (to reset after submission)
  const [isFormatFromAiTools, setIsFormatFromAiTools] = useState<boolean>(false);

  // Sync with initialOutputFormat when it changes (from parent)
  useEffect(() => {
    if (initialOutputFormat) {
      setSelectedOutputFormat(initialOutputFormat);
      setIsFormatFromAiTools(false); // Parent changes don't count as AI Tools
    }
  }, [initialOutputFormat]);

  // Effect to restore focus after typing effects complete
  useEffect(() => {
    if (shouldRestoreFocus && !fetchMatchDocumentsPending && !generateAnswersPending) {
      const timer = setTimeout(() => {
        if (inpRef.current && !document.activeElement?.closest('#ai-search-input')) {
          inpRef.current.focus();
        }
        setShouldRestoreFocus(false);
      }, 300); // Small delay to ensure typing effect is complete

      return () => clearTimeout(timer);
    }
  }, [shouldRestoreFocus, fetchMatchDocumentsPending, generateAnswersPending]);

  // Set flag to restore focus when search starts
  useEffect(() => {
    if (fetchMatchDocumentsPending || generateAnswersPending) {
      setShouldRestoreFocus(true);
    }
  }, [fetchMatchDocumentsPending, generateAnswersPending]);

  // Handle Escape key to close selected bundles popper
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && openSelectedBundlesMenu) {
        setSelectedBundlesAnchorEl(null);
      }
    };

    if (openSelectedBundlesMenu) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [openSelectedBundlesMenu]);

  const handleRecentSearchesMenuClose = () => {
    setRecentSearchesAnchorEl(null);
  };

  const handleOutputFormatMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    if (outputFormatTimeoutRef.current) {
      clearTimeout(outputFormatTimeoutRef.current);
      outputFormatTimeoutRef.current = null;
    }
    setOutputFormatAnchorEl(event.currentTarget);
  };

  const handleOutputFormatMouseLeave = () => {
    outputFormatTimeoutRef.current = setTimeout(() => {
      setOutputFormatAnchorEl(null);
    }, 200);
  };

  const handleOutputFormatSelect = (format: string) => {
    setSelectedOutputFormat(format);
    setIsFormatFromAiTools(false); // Manual selection from menu, don't reset
    // Notify parent component if callback is provided
    if (onOutputFormatChange) {
      onOutputFormatChange(format);
    }
  };

  const startSearch = (input: string) => {
    // Process the input based on selected output format
    const finalInput = input.trim();

    // Use selectedOutputFormat with fallback to 'list'
    const outputType = selectedOutputFormat || 'list';
    handleSearch(finalInput, filters, outputType);

    // Reset output format to 'list' if it was set from AI Tools Dialog
    if (isFormatFromAiTools) {
      setSelectedOutputFormat('list');
      setIsFormatFromAiTools(false);
      // Notify parent if callback exists
      if (onOutputFormatChange) {
        onOutputFormatChange('list');
      }
    }

    // Clear phrase and prompt metadata after search
    handleChangeFilters({
      phrase: '',
      promptCategory: undefined,
      promptName: undefined,
    });
  };

  const handleSaveFiles = ({ files }: { files: FileItem[]; bundles: any[] }) => {
    handleChangeFilters({
      files,
      isCompareMode: compareFilesMode,
    });
    setFilesPopupOpen(false);
  };

  const handleCloseFilesPopup = () => {
    setFilesPopupOpen(false);
  };

  return (
    <div>
      <style>
        {`
          .locations-scrollbar::-webkit-scrollbar,
          .files-scrollbar::-webkit-scrollbar {
            width: 6px;
          }
          
          .locations-scrollbar::-webkit-scrollbar-track,
          .files-scrollbar::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 10px;
          }
          
          .locations-scrollbar::-webkit-scrollbar-thumb,
          .files-scrollbar::-webkit-scrollbar-thumb {
            background: #c1c1c1;
            border-radius: 10px;
          }
          
          .locations-scrollbar::-webkit-scrollbar-thumb:hover,
          .files-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #a8a8a8;
          }
        `}
      </style>
      {isNewSearch &&
        isAiToolsPage &&
        (filters.location?.length > 0 || filters.files?.length > 0 || !!filters.phrase?.trim()) && (
          <div
            style={{
              marginTop: '-20px',
              paddingLeft: '10px',
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <Button
              size="small"
              startIcon={
                <SettingsBackupRestoreOutlined fontSize="small" sx={{ color: '#85A0F1' }} />
              }
              onClick={() => {
                // Remove askAiBundle / askAIBundle query params from URL on reset (without reload)
                try {
                  const url = new URL(window.location.href);
                  let changed = false;
                  if (url.searchParams.has('askAiBundle')) {
                    url.searchParams.delete('askAiBundle');
                    changed = true;
                  }
                  if (url.searchParams.has('askAIBundle')) {
                    url.searchParams.delete('askAIBundle');
                    changed = true;
                  }
                  if (changed) {
                    const newSearch = url.searchParams.toString();
                    const newUrl = url.pathname + (newSearch ? `?${newSearch}` : '') + url.hash;
                    window.history.replaceState(null, '', newUrl);
                  }
                } catch {
                  // silently ignore URL parsing issues
                }
                setFilters({
                  location: [],
                  files: [],
                  isCompareMode: false,
                  phrase: '',
                  searchHistoryId: '',
                  question: '',
                  triggerSearch: false,
                  promptCategory: '',
                  promptName: '',
                });
                setTimeout(() => inpRef.current?.focus(), 100);
              }}
              sx={{
                textTransform: 'none',
                pl: 0,
                border: '1px solid #85A0F1',
                padding: '1px 4px',
                margin: '5px 2px',
                whiteSpace: 'nowrap',
                fontSize: '12px',
                '& .MuiButton-startIcon': {
                  marginRight: '2px',
                },
              }}
            >
              {T.translate('generic.clear')}
            </Button>
          </div>
        )}
      <div
        style={{
          border: '1px  solid #0000003B',
          borderRadius: '0.5rem',
          height: 'auto',
          padding: gridView ? '0.1 rem' : '0.5rem',
          marginLeft: '10px',
          backgroundColor: '#FFF',
          // Visual feedback for bundle drop
          ...(canDropBundle && isOverBundle
            ? {
                borderColor: '#4567E5',
                boxShadow: '0 0 0 2px rgba(69,103,229,0.3)',
              }
            : {}),
        }}
        ref={dropRef}
      >
        <TextField
          inputRef={inpRef}
          id="ai-search-input"
          variant={'standard'}
          fullWidth
          multiline
          maxRows={isNewSearch ? 8 : fileSidebarAskAI ? 8 : 3}
          placeholder={
            fileSidebarAskAI
              ? filters?.files?.length > 1
                ? T.translate('case.askAnythingAboutTheSelectedDocuments')
                : filters.location?.length > 0
                  ? T.translate('case.askAnythingAboutTheSelectedBundle')
                  : T.translate('case.askAnythingAboutTheDocument')
              : isNewSearch
                ? T.translate('case.topicInputPlaceholder')
                : T.translate('case.questionInputPlaceholder')
          }
          sx={{
            borderRadius: '1rem',
            '.MuiOutlinedInput-root': { p: 0 },
            padding: isNewSearch ? '0.1rem' : '0.5rem',
            // Remove container scrolling to prevent double scroll
            '& .MuiInputBase-root': {
              overflow: 'visible',
            },
            // Let the textarea handle scrolling naturally
            '& .MuiInputBase-inputMultiline': {
              resize: 'none',
            },
          }}
          value={filters.phrase}
          onChange={(e) => {
            handleChangeFilters({ phrase: e.target.value });
            if (e.target.value && e.target.value.length > 0 && !openRecentSearches) {
              setRecentSearchesAnchorEl(e.currentTarget);
            } else if (openRecentSearches && e.target.value.length === 0) {
              setRecentSearchesAnchorEl(null);
            }
          }}
          onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
            e.stopPropagation();

            // Allow Shift+Enter for new line
            if (e.key === 'Enter' && e.shiftKey) {
              // Let the default behavior happen (new line)
              return;
            }

            // Handle Enter key for submission
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault(); // Prevent default to avoid newline
              // Check if submission is allowed (same conditions as SendOutlined button)
              const isSubmitDisabled =
                filters.phrase?.length === 0 ||
                (filters.isCompareMode && filters.files && filters.files.length < 2);

              if (!isSubmitDisabled && filters.phrase) {
                startSearch(filters.phrase);
              }
            }
          }}
          onClick={() => inpRef.current?.focus()}
          autoFocus
          autoComplete="off"
          disabled={fetchMatchDocumentsPending || generateAnswersPending}
          InputProps={{ disableUnderline: true, sx: { fontSize: '1rem' } }}
        />
        <div
          style={{
            marginTop: '0.5rem',
            paddingLeft: fileSidebarAskAI && !isNewSearch ? '0.5rem' : '0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <Stack direction="row" spacing={1}>
              <Chip
                clickable
                size="small"
                icon={<MarginOutlined />}
                sx={{
                  backgroundColor: '#F7F7F8',
                  fontSize: '1rem',
                  maxWidth: '20rem',
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                }}
                label={`${T.translate('generic.answerFormat')}: ${getOutputFormatLabel(selectedOutputFormat)}`}
                onMouseEnter={handleOutputFormatMouseEnter}
                onMouseLeave={handleOutputFormatMouseLeave}
                onClick={(e) => setOutputFormatAnchorEl(e.currentTarget)}
              />
              {(isNewSearch || (directAnswers && isAiToolsPage)) && (
                <>
                  {!filters.isCompareMode && !file && isAiToolsPage && (
                    <Chip
                      size="small"
                      icon={<FilterListOutlined />}
                      sx={{
                        backgroundColor: '#F7F7F8',
                        fontSize: '1rem',
                        maxWidth: file ? '25rem' : '35rem',
                        '& .MuiChip-label': {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        },
                      }}
                      label={
                        <SearchFiltersChip
                          filters={filters}
                          isAnswersMode={isAnswersMode}
                          file={file}
                          type="location"
                          fileSidebarAskAI={fileSidebarAskAI}
                        />
                      }
                      clickable
                      onClick={(e) => {
                        setFiltersMenu(e.currentTarget);
                        setCompareFilesMode(false);
                      }}
                    />
                  )}
                  {(!filters.isCompareMode || (isNewSearch && !isAiToolsPage)) &&
                    ((!file && isAiToolsPage) || file) && (
                      <Chip
                        size="small"
                        icon={<FilterListOutlined />}
                        sx={{
                          backgroundColor: '#F7F7F8',
                          fontSize: '1rem',
                          maxWidth: file ? '25rem' : '35rem',
                          '& .MuiChip-label': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          },
                        }}
                        label={
                          <SearchFiltersChip
                            filters={filters}
                            isAnswersMode={isAnswersMode}
                            file={file}
                            type="files"
                            fileSidebarAskAI={fileSidebarAskAI}
                          />
                        }
                        clickable
                        onClick={() => {
                          setCompareFilesMode(false);
                          setFilesPopupOpen(true);
                        }}
                      />
                    )}
                  {showCompareChip && (
                    <Chip
                      size="small"
                      icon={<Compare />}
                      sx={{
                        backgroundColor: '#F7F7F8',
                        fontSize: '1rem',
                        color: '#000',
                        maxWidth: file ? '25rem' : '35rem',
                        '& .MuiChip-label': {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        },
                      }}
                      label={
                        <SearchFiltersChip
                          filters={filters}
                          isAnswersMode={isAnswersMode}
                          file={file}
                          type="compareFiles"
                          fileSidebarAskAI={fileSidebarAskAI}
                        />
                      }
                      clickable
                      onClick={() => {
                        setCompareFilesMode(true);
                        setFilesPopupOpen(true);
                      }}
                    />
                  )}
                </>
              )}
              {!fileSidebarAskAI && (
                <>
                  {(directAnswers || isNewSearch) && useAiPromptLibrary && (
                    <Chip
                      size="small"
                      icon={<ConstructionOutlined />}
                      sx={{
                        backgroundColor: '#F7F7F8',
                        fontSize: '1rem',
                        maxWidth: '10rem',
                        '& .MuiChip-label': {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        },
                      }}
                      label={T.translate('generic.promptLibrary')}
                      clickable
                      onClick={() => setAiToolsDialogOpen(true)}
                    />
                  )}
                  <Chip
                    size="small"
                    icon={<TuneOutlined />}
                    sx={{
                      backgroundColor: '#F7F7F8',
                      fontSize: '1rem',
                      maxWidth: '10rem',
                      '& .MuiChip-label': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      },
                    }}
                    label={T.translate('generic.options')}
                    clickable
                    onClick={(e) => setSearchOptionsAnchorEl(e.currentTarget)}
                  />
                  {!isNewSearch &&
                    !directAnswers &&
                    searchMatchesInput?.[0]?.location?.length > 0 && (
                      <Chip
                        size="small"
                        icon={<FolderOutlined />}
                        sx={{
                          backgroundColor: '#F7F7F8',
                          fontSize: '1rem',
                          maxWidth: '25rem',
                          '& .MuiChip-label': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          },
                        }}
                        label={(() => {
                          const bundleLocations =
                            directAnswersFilters?.location || searchMatchesInput?.[0]?.location;
                          const firstBundleName = bundleLocations?.[0]?.name || '';
                          const isTruncated = firstBundleName.length > 20;
                          const truncatedName = isTruncated
                            ? `${firstBundleName.substring(0, 20)}...`
                            : firstBundleName;
                          const suffix = isTruncated ? '' : ', ...';
                          return `${T.translate('generic.selectedBundles')}: ${truncatedName}${suffix}`;
                        })()}
                        clickable
                        onClick={(e) => setSelectedBundlesAnchorEl(e.currentTarget)}
                      />
                    )}
                </>
              )}
            </Stack>
          </div>

          <IconButton
            disabled={
              filters.phrase?.length === 0 ||
              (filters.isCompareMode &&
                filters.files &&
                !fileSidebarAskAI &&
                filters.files.length < 2)
            }
            onClick={() => startSearch(filters.phrase)}
          >
            <SendOutlined />
          </IconButton>
        </div>
        {openSearchOptions && searchOptionsAnchorEl && !fileSidebarAskAI && (
          <SearchOptions
            open={openSearchOptions}
            anchorEl={searchOptionsAnchorEl}
            directAnswersForChatEnabled={directAnswers}
            isNewSearch={isNewSearch}
            handleClose={() => {
              setSearchOptionsAnchorEl(null);
              const searchInp = document.getElementById('ai-search-input');
              if (searchInp) {
                searchInp.click();
              }
            }}
          />
        )}
        <TrialBundlesMenu
          value={filters.location}
          onChange={(newValue: any) => handleChangeFilters({ location: newValue })}
          anchorEl={filtersMenu}
          isOpen={openFiltersMenu}
          onClose={() => setFiltersMenu(null)}
        />
        {openOutputFormatMenu && outputFormatAnchorEl && (
          <OutputFormatMenu
            anchorEl={outputFormatAnchorEl}
            open={openOutputFormatMenu}
            onClose={() => setOutputFormatAnchorEl(null)}
            onSelect={handleOutputFormatSelect}
            onMouseEnter={() => {
              if (outputFormatTimeoutRef.current) {
                clearTimeout(outputFormatTimeoutRef.current);
                outputFormatTimeoutRef.current = null;
              }
            }}
            onMouseLeave={handleOutputFormatMouseLeave}
          />
        )}
        {openSelectedBundlesMenu && selectedBundlesAnchorEl && (
          <ClickAwayListener onClickAway={() => setSelectedBundlesAnchorEl(null)}>
            <Popper
              open={openSelectedBundlesMenu}
              anchorEl={selectedBundlesAnchorEl}
              placement="top-start"
              modifiers={[
                {
                  name: 'offset',
                  options: {
                    offset: [0, 8],
                  },
                },
              ]}
            >
              <Paper
                sx={{
                  maxHeight: 400,
                  minWidth: 250,
                  maxWidth: 400,
                  boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.15)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    padding: '12px 16px',
                    borderBottom: '1px solid #e0e0e0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 'bold',
                      color: 'text.secondary',
                      fontSize: '13px',
                    }}
                  >
                    {T.translate('generic.totalSelectedBundles')}
                  </Typography>
                  <span
                    style={{
                      paddingTop: '2px',
                      paddingBottom: '2px',
                      paddingLeft: '8px',
                      paddingRight: '8px',
                      backgroundColor: '#F7F7F8',
                      border: '1px solid #85A0F1',
                      borderRadius: '16px',
                      fontSize: '12px',
                      lineHeight: '1',
                      minWidth: '24px',
                      textAlign: 'center',
                      display: 'inline-block',
                    }}
                  >
                    {directAnswersFilters?.location?.length ||
                      searchMatchesInput?.[0]?.location?.length ||
                      0}
                  </span>
                </div>
                <div
                  style={{
                    maxHeight: '320px',
                    overflowY: 'auto',
                    padding: '16px',
                  }}
                >
                  {(directAnswersFilters?.location || searchMatchesInput?.[0]?.location)?.map(
                    (bundle: any) => (
                      <div
                        key={bundle.id}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '8px',
                          paddingTop: '8px',
                          paddingBottom: '8px',
                        }}
                      >
                        <span
                          style={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            backgroundColor: '#9e9e9e',
                            marginTop: '6px',
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.primary',
                            fontSize: '13px',
                            lineHeight: '1.4',
                          }}
                        >
                          {bundle.name}
                        </Typography>
                      </div>
                    ),
                  )}
                </div>
              </Paper>
            </Popper>
          </ClickAwayListener>
        )}
        {openRecentSearches && recentSearchesAnchorEl && filters?.phrase && (
          <RecentSearchesList
            handleRecentSearchesMenuClose={handleRecentSearchesMenuClose}
            anchorEl={recentSearchesAnchorEl}
            openRecentSearchesMenu={openRecentSearches}
            isAnswersMode={isAnswersMode}
            handleChangeFilters={handleChangeFilters}
            currentInput={filters.phrase}
          />
        )}

        {/* Files Selection Dialog */}
        <FilesAndBundlesSelectionDialog
          open={filesPopupOpen}
          onClose={handleCloseFilesPopup}
          onSave={handleSaveFiles}
          initialSelectedFiles={(filters.files as FileItem[]) || []}
          initialSelectedBundles={[]}
          allowedMode="files"
          compareMode={compareFilesMode}
          lockedAnchorFile={
            fileSidebarAskAI && compareFilesMode && file
              ? { id: file.id || file.fileId, name: file.name || file.id || file.fileId }
              : null
          }
        />

        {/* AI Tools Dialog */}
        <AiToolsDialog
          open={aiToolsDialogOpen}
          directAnswers={directAnswers}
          filters={filters}
          onClose={() => setAiToolsDialogOpen(false)}
          onSaveSelections={({ files, prompt, location }) => {
            // Single unified update: always set files/isCompareMode; conditionally add prompt fields
            if (prompt?.outputType) {
              const format = prompt.outputType.toLowerCase();
              setSelectedOutputFormat(format);
              setIsFormatFromAiTools(true);
              if (onOutputFormatChange) {
                onOutputFormatChange(format);
              }
            }

            // Robust reset logic driven by prompt.filterType flags
            const ft = (prompt?.filterType || {}) as any;
            const allowDocuments = !!ft.documents;
            const allowBundles = !!ft.bundles;
            const isComparison = !!ft.isComparison;
            const isSticky = !!ft.sticky;
            const isAnyFilesOrBundles = !!ft.anyFilesOrBundles;

            // Build update object - only update fields that the prompt actually uses
            // Preserve existing filter values for fields the prompt doesn't require
            const unifiedUpdate: any = {};

            // Always update prompt-related fields if prompt exists
            if (prompt) {
              unifiedUpdate.phrase = prompt.prompt;
              unifiedUpdate.promptCategory = prompt.category;
              unifiedUpdate.promptName = prompt.name;
            }

            // Only update files/isCompareMode if prompt requires documents
            if (
              allowDocuments ||
              ft.multipleDocuments ||
              isSticky ||
              (isAnyFilesOrBundles && files && files.length > 0)
            ) {
              unifiedUpdate.files = files || [];
              unifiedUpdate.isCompareMode = isComparison;
            }
            // If it's a comparison prompt, always set comparison mode
            if (isComparison) {
              unifiedUpdate.isCompareMode = true;
              if (files && files.length) {
                unifiedUpdate.files = files;
              }
            }

            // Only update location if prompt requires bundles
            if (
              allowBundles ||
              ft.multipleBundles ||
              (isAnyFilesOrBundles && location && location.length > 0)
            ) {
              unifiedUpdate.location = location || [];
            }

            // Mutual exclusivity for anyFilesOrBundles: clear the other selection
            if (isAnyFilesOrBundles) {
              if (location && location.length > 0) {
                unifiedUpdate.files = [];
                unifiedUpdate.isCompareMode = false;
              } else if (files && files.length > 0) {
                unifiedUpdate.location = [];
              }
            }

            handleChangeFilters(unifiedUpdate);
            // Dialog will close itself after save via onClose; refocus input slightly after
            setTimeout(() => {
              if (inpRef.current) {
                inpRef.current.focus();
              }
            }, 120);
          }}
        />
      </div>
      <div style={{ marginTop: '0.3rem', padding: '10px' }}>
        {filters.isCompareMode &&
          filters.files &&
          filters.files.length > 1 &&
          multiComparison &&
          anchorFileRemoved && (
            <Typography
              variant="body2"
              sx={{
                fontSize: '12px',
                color: '#4567e5',
                fontWeight: 'medium',
                marginBottom: '0.5rem',
              }}
            >
              {T.translate('generic.anchorFileDeletedInfo')}
            </Typography>
          )}
        {filters.isCompareMode &&
          filters.files &&
          !fileSidebarAskAI &&
          filters.files.length === 1 && (
            <Typography
              variant="body2"
              sx={{
                fontSize: '13px',
                color: '#d32f2f',
                fontWeight: 'medium',
              }}
            >
              {T.translate('generic.forComparisonMinimumTwoFilesRequired')}
            </Typography>
          )}
      </div>
      {((isNewSearch && !fileSidebarAskAI) || fileSidebarAskAI) && (
        <div style={{ marginTop: '0.3rem', paddingLeft: '10px' }}>
          {(filters.location?.length > 0 ||
            filters.files?.length > 0 ||
            (fileSidebarAskAI && fileData)) && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: (() => {
                  // Check how many columns will be displayed
                  const hasLocations = filters.location?.length > 0 && !filters.isCompareMode;
                  const hasFiles = filters.files?.length > 0 || (fileSidebarAskAI && fileData);

                  // If both columns are present, use 1fr 1fr
                  if (hasLocations && hasFiles) {
                    return '1fr 1fr';
                  }
                  // If only one column is present, use full width
                  return '1fr';
                })(),
                gap: '1rem',
                maxHeight: '300px',
              }}
            >
              {/* Locations Column */}
              {isNewSearch && filters.location?.length > 0 && !filters.isCompareMode && !file && (
                <div
                  style={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '0.5rem',
                    padding: '1rem 0.5rem',
                    backgroundColor: '#fafafa',
                  }}
                >
                  <Typography variant="h6" sx={{ marginBottom: '0.5rem', fontWeight: 'bold' }}>
                    {T.translate('case.bundles')} ({filters.location.length})
                  </Typography>
                  <div
                    style={{
                      maxHeight: '15rem',
                      overflowY: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.5rem',
                      scrollbarWidth: 'thin',
                      scrollbarColor: '#c1c1c1 #f1f1f1',
                    }}
                    className="locations-scrollbar"
                  >
                    {filters.location.map((location: any) => (
                      <div
                        key={location.id}
                        style={{
                          padding: '0.3rem',
                          border: '1px solid #d0d0d0',
                          borderRadius: '0.5rem',
                          backgroundColor: '#fff',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: '0.5rem',
                          minHeight: '3rem',
                        }}
                      >
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 'bold',
                              wordBreak: 'break-word',
                              marginBottom: '0.25rem',
                            }}
                          >
                            {location.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'text.secondary',
                              fontFamily: 'monospace',
                            }}
                          >
                            ID: {location.id}
                          </Typography>
                        </div>
                        <Tooltip title={T.translate('generic.remove')}>
                          <IconButton
                            size="small"
                            aria-label="remove-bundle"
                            onClick={() => handleRemoveLocation(location.id)}
                          >
                            <CloseOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Files Column */}
              {filters.files?.length > 0 && (
                <div
                  style={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '0.5rem',
                    padding: '1rem 0.5rem',
                    backgroundColor: '#fafafa',
                  }}
                >
                  <Typography variant="h6" sx={{ marginBottom: '0.5rem', fontWeight: 'bold' }}>
                    {filters.isCompareMode ||
                    askAiSearchType === 'compareFiles' ||
                    (file && fileSidebarAskAI)
                      ? T.translate('generic.compare')
                      : T.translate('case.files')}{' '}
                    ({!fileSidebarAskAI || !file ? filters.files.length : filters.files.length + 1})
                  </Typography>
                  <div
                    style={{
                      maxHeight: '15rem',
                      overflowY: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.5rem',
                      scrollbarWidth: 'thin',
                      scrollbarColor: '#c1c1c1 #f1f1f1',
                    }}
                    className="files-scrollbar"
                  >
                    {fileSidebarAskAI && fileData?.id && (
                      <div
                        key={fileData.id}
                        style={{
                          padding: '0.3rem',
                          border: '1px solid #d0d0d0',
                          borderRadius: '0.25rem',
                          backgroundColor: '#fff',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 'bold',
                            wordBreak: 'break-word',
                            marginBottom: '0.25rem',
                          }}
                        >
                          {fileData.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            fontFamily: 'monospace',
                          }}
                        >
                          ID: {fileData.id}
                        </Typography>
                      </div>
                    )}
                    {multiComparison && filters.isCompareMode && !fileSidebarAskAI ? (
                      <>
                        {/* Anchor File Section */}
                        {filters.files.length > 0 && (
                          <div style={{ marginBottom: '1rem' }}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontSize: '12px',
                                fontWeight: 'bold',
                                color: 'text.primary',
                                marginBottom: '0.5rem',
                              }}
                            >
                              {T.translate('generic.anchorFile')}
                            </Typography>
                            <div
                              key={filters.files[0].id}
                              style={{
                                padding: '0.3rem',
                                border: '1px solid #d0d0d0',
                                borderRadius: '0.25rem',
                                backgroundColor: '#fff',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: '0.5rem',
                              }}
                            >
                              <div style={{ flex: 1 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 'bold',
                                    wordBreak: 'break-word',
                                    marginBottom: '0.25rem',
                                  }}
                                >
                                  {filters.files[0].name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: 'text.secondary',
                                    fontFamily: 'monospace',
                                  }}
                                >
                                  ID: {filters.files[0].id}
                                </Typography>
                              </div>
                              <div
                                style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
                              >
                                <Tooltip title={T.translate('case.viewDocInNewTab')}>
                                  <Button
                                    size="small"
                                    onClick={() => handleViewDocument(filters.files[0].id)}
                                    sx={{
                                      backgroundColor: '#4567E50A',
                                      color: '#7BA0F4',
                                      minWidth: 'auto',
                                      px: 1,
                                      fontSize: '12px',
                                      textTransform: 'none',
                                      '&:hover': {
                                        color: '#3048a0',
                                      },
                                    }}
                                  >
                                    {T.translate('case.preview')}
                                  </Button>
                                </Tooltip>
                                <Tooltip title={T.translate('generic.remove')}>
                                  <IconButton
                                    size="small"
                                    aria-label="remove-file"
                                    onClick={() => handleRemoveFile(filters.files[0].id)}
                                  >
                                    <CloseOutlined fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Comparison Files Section */}
                        {filters.files.length > 1 && (
                          <div>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontSize: '12px',
                                fontWeight: 'bold',
                                color: 'text.primary',
                                marginBottom: '0.5rem',
                              }}
                            >
                              {T.translate('generic.comparisonFiles')}
                            </Typography>
                            {filters.files.slice(1).map((fileItem: any) => (
                              <div
                                key={fileItem.id}
                                style={{
                                  padding: '0.3rem',
                                  border: '1px solid #d0d0d0',
                                  borderRadius: '0.25rem',
                                  backgroundColor: '#fff',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  gap: '0.5rem',
                                  marginBottom: '0.5rem',
                                }}
                              >
                                <div style={{ flex: 1 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 'bold',
                                      wordBreak: 'break-word',
                                      marginBottom: '0.25rem',
                                    }}
                                  >
                                    {fileItem.name}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: 'text.secondary',
                                      fontFamily: 'monospace',
                                    }}
                                  >
                                    ID: {fileItem.id}
                                  </Typography>
                                </div>
                                <div
                                  style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
                                >
                                  <Tooltip title={T.translate('case.viewDocInNewTab')}>
                                    <Button
                                      size="small"
                                      onClick={() => handleViewDocument(fileItem.id)}
                                      sx={{
                                        backgroundColor: '#4567E50A',
                                        color: '#7BA0F4',
                                        minWidth: 'auto',
                                        px: 1,
                                        fontSize: '12px',
                                        textTransform: 'none',
                                        '&:hover': {
                                          color: '#3048a0',
                                        },
                                      }}
                                    >
                                      {T.translate('case.preview')}
                                    </Button>
                                  </Tooltip>
                                  <Tooltip title={T.translate('generic.remove')}>
                                    <IconButton
                                      size="small"
                                      aria-label="remove-file"
                                      onClick={() => handleRemoveFile(fileItem.id)}
                                    >
                                      <CloseOutlined fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      // Default display when not in multi-comparison mode
                      filters.files.map((fileItem: any) => (
                        <div
                          key={fileItem.id}
                          style={{
                            padding: '0.3rem',
                            border: '1px solid #d0d0d0',
                            borderRadius: '0.25rem',
                            backgroundColor: '#fff',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: '0.5rem',
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 'bold',
                                wordBreak: 'break-word',
                                marginBottom: '0.25rem',
                              }}
                            >
                              {fileItem.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.secondary',
                                fontFamily: 'monospace',
                              }}
                            >
                              ID: {fileItem.id}
                            </Typography>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            <Tooltip title={T.translate('case.viewDocInNewTab')}>
                              <Button
                                size="small"
                                onClick={() => handleViewDocument(fileItem.id)}
                                sx={{
                                  backgroundColor: '#4567E50A',
                                  color: '#7BA0F4',
                                  minWidth: 'auto',
                                  px: 1,
                                  fontSize: '12px',
                                  textTransform: 'none',
                                  '&:hover': {
                                    color: '#3048a0',
                                  },
                                }}
                              >
                                {T.translate('case.preview')}
                              </Button>
                            </Tooltip>
                            <Tooltip title={T.translate('generic.remove')}>
                              <IconButton
                                size="small"
                                aria-label="remove-file"
                                onClick={() => handleRemoveFile(fileItem.id)}
                              >
                                <CloseOutlined fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
